import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createSeasonSchema, type CreateSeasonInput } from '@proref/schemas';
import type { Season } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Field } from '../../components/shared/Field';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import { useSeasonsQuery, useCreateSeason, useUpdateSeason, useDeleteSeason } from '../../hooks/use-seasons';

// today() as YYYY-MM-DD, for the "current" badge — a plain local read is fine here (unlike the
// leaderboard's own window math, this is cosmetic: which row highlights as current on THIS
// admin's screen, not a boundary anything is computed against).
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Add or edit a season. One dialog serves both flows: `season` present → edit (PATCH by id);
// absent → create. super_admin only — the route is gated, and this page only renders inside the
// super_admin console. Overlap (409 SEASON_OVERLAP) surfaces via notifyError's fallback message.
function SeasonFormDialog({ season, trigger }: { season?: Season; trigger: ReactNode }) {
  const { t } = useTranslation();
  const isEdit = Boolean(season);
  const createSeason = useCreateSeason();
  const updateSeason = useUpdateSeason();
  const [open, setOpen] = useState(false);

  const defaults: CreateSeasonInput = season
    ? { name: season.name, starts_on: season.starts_on, ends_on: season.ends_on }
    : { name: '', starts_on: '', ends_on: '' };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateSeasonInput>({ resolver: zodResolver(createSeasonSchema), defaultValues: defaults });

  const pending = createSeason.isPending || updateSeason.isPending;

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset(defaults);
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (season) {
        await updateSeason.mutateAsync({ id: season.id, input: values });
        notifySuccess(t('admin.seasons.updatedToast', { name: values.name }));
      } else {
        const created = await createSeason.mutateAsync(values);
        notifySuccess(t('admin.seasons.addedToast', { name: created.name }));
      }
      onOpenChange(false);
    } catch (err) {
      notifyError(err, isEdit ? t('admin.seasons.updateErrorToast') : t('admin.seasons.addErrorToast'));
    }
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isEdit ? t('admin.seasons.editTitle', { name: season!.name }) : t('admin.seasons.addTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('admin.seasons.formDescription')}</AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
          <Field label={t('admin.seasons.fieldName')} htmlFor="season-name" error={errors.name?.message}>
            <Input id="season-name" placeholder={t('admin.seasons.namePlaceholder')} {...register('name')} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('admin.seasons.fieldStart')} htmlFor="season-start" error={errors.starts_on?.message}>
              <Input id="season-start" type="date" {...register('starts_on')} />
            </Field>
            <Field label={t('admin.seasons.fieldEnd')} htmlFor="season-end" error={errors.ends_on?.message}>
              <Input id="season-end" type="date" {...register('ends_on')} />
            </Field>
          </div>

          <AlertDialogFooter className="mt-1">
            <AlertDialogCancel type="button" disabled={pending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <Button type="submit" variant="accent" disabled={pending}>
              {pending ? (
                <Spinner className="w-4 h-4" />
              ) : isEdit ? (
                t('admin.users.editDialog.saveChanges')
              ) : (
                t('admin.seasons.addSeason')
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteSeasonDialog({ season }: { season: Season }) {
  const { t } = useTranslation();
  const del = useDeleteSeason();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await del.mutateAsync(season.id);
      notifySuccess(t('admin.seasons.deletedToast', { name: season.name }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.seasons.deleteErrorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" icon aria-label={t('admin.seasons.deleteAria', { name: season.name })}>
          <Icon.trash className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.seasons.deleteConfirmTitle', { name: season.name })}</AlertDialogTitle>
          <AlertDialogDescription>{t('admin.seasons.deleteConfirmBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={del.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={confirm} disabled={del.isPending}>
            {del.isPending ? <Spinner className="w-4 h-4" /> : t('admin.seasons.deleteButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SeasonsList() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useSeasonsQuery();
  const seasons = data ?? [];
  const today = todayLocal();

  if (isLoading) return <ListSkeleton rows={4} />;

  if (isError && !data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.seasons.couldNotLoad'))}
      </div>
    );
  }

  if (seasons.length === 0) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
        <Icon.calendar className="mb-2 h-6 w-6 text-muted" />
        <p className="text-sm text-muted">{t('admin.seasons.noSeasonsYet')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-line bg-surface">
      <table className="w-full min-w-[520px] text-left text-[13px]">
        <thead>
          <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
            <th className="px-4 py-2.5 font-semibold">{t('admin.seasons.colName')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.seasons.colStart')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.seasons.colEnd')}</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {seasons.map((season) => {
            // Highlights the row the Leaderboard's period='season' board is currently reading from
            // — the same [starts_on, ends_on] inclusive check the API applies server-side.
            const current = season.starts_on <= today && today <= season.ends_on;
            return (
              <tr key={season.id} className="group border-b border-line last:border-0">
                <td className="px-4 py-2.5 font-medium text-ink">
                  <span className="flex items-center gap-2">
                    {season.name}
                    {current && (
                      <span className="rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-accent-ink">
                        {t('admin.seasons.current')}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{season.starts_on}</td>
                <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{season.ends_on}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <SeasonFormDialog
                      season={season}
                      trigger={
                        <Button variant="ghost" size="sm" icon aria-label={t('admin.seasons.editAria', { name: season.name })}>
                          <Icon.pencil className="w-3.5 h-3.5" />
                        </Button>
                      }
                    />
                    <DeleteSeasonDialog season={season} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function SeasonsPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        title={t('admin.seasons.title')}
        sub={t('admin.seasons.sub')}
        actions={
          <SeasonFormDialog
            trigger={
              <Button variant="accent">
                <Icon.plus className="w-3.5 h-3.5" /> {t('admin.seasons.addSeasonButton')}
              </Button>
            }
          />
        }
      />

      <div className="mb-5 flex items-start gap-2.5 rounded border border-line bg-bg-2 px-4 py-3">
        <Icon.spark className="mt-0.5 w-4 h-4 flex-none text-accent" />
        <p className="text-[12.5px] text-muted">{t('admin.seasons.noteBody')}</p>
      </div>

      <SeasonsList />
    </>
  );
}
